import GridQuestionSchema from './schemas/GridQuestionSchema';
import { GridRequiredStrategy } from './schemas/GridRequiredOptions';
import QuestionSchema from './schemas/QuestionSchema';

enum QuestionType {
  Empty = 'Empty',
  CheckboxList = 'CheckboxList',
  CheckboxGrid = 'CheckboxGrid',
  DropdownMenu = 'DropdownMenu',
  OpenEndedText = 'OpenEndedText',
  OpenEndedNumeric = 'OpenEndedNumeric',
  RadioButtonGrid = 'RadioButtonGrid',
  RadioButtonGridRow = 'RadioButtonGridRow',
  RadioButtonList = 'RadioButtonList',
  RatingList = 'RatingList',
  RankingList = 'RankingList',
  TextboxGridNumeric = 'TextboxGridNumeric',
  TextboxListNumeric = 'TextboxListNumeric',
  TextboxListText = 'TextboxListText',
  ContinuousSumList = 'ContinuousSumList',
}
export default QuestionType;

export function getQuestionTypeText(qType: QuestionType) {
  switch (qType) {
    case QuestionType.Empty:
      return null;
    case QuestionType.RankingList:
      return 'Ranking';
    case QuestionType.CheckboxList:
      return 'Select all that apply';
    case QuestionType.CheckboxGrid:
      return 'Select all that apply grid';
    case QuestionType.DropdownMenu:
      return 'Dropdown menu';
    case QuestionType.OpenEndedText:
      return 'Open-end text (single)';
    case QuestionType.OpenEndedNumeric:
      return 'Numeric (single)';
    case QuestionType.RadioButtonGrid:
      return 'Select one grid';
    case QuestionType.RadioButtonGridRow:
      return 'Select one per row';
    case QuestionType.RadioButtonList:
      return 'Select one';
    case QuestionType.RatingList:
      return 'Rating';
    case QuestionType.TextboxGridNumeric:
      return 'Numeric grid';
    case QuestionType.TextboxListNumeric:
      return 'Numeric (multiple)';
    case QuestionType.TextboxListText:
      return 'Open-end text (multiple)';
    case QuestionType.ContinuousSumList:
      return 'Sum to 100 points';
    default:
      throw Error(`Unknown QuestionType (${qType})`);
  }
}

export function getQuestionInstructions(
  qSchema: QuestionSchema,
  isDesktop: boolean
): string | undefined {
  switch (qSchema.questionType) {
    case QuestionType.Empty:
      return '';
    case QuestionType.CheckboxList:
      return 'Select all that apply';
    case QuestionType.CheckboxGrid:
      return (
        getGridHelperText(qSchema, isDesktop) ||
        'Select all that apply in each row'
      );
    case QuestionType.DropdownMenu:
      return 'Select one';
    case QuestionType.OpenEndedText:
      return 'Write your answer in the space below';
    case QuestionType.OpenEndedNumeric:
      return 'Write your answer in the field below';
    case QuestionType.RadioButtonGrid:
      return getGridHelperText(qSchema, isDesktop) || 'Select one per column';
    case QuestionType.RadioButtonGridRow:
      return getGridHelperText(qSchema, isDesktop) || 'Select one per row';
    case QuestionType.RadioButtonList:
      return 'Select one';
    case QuestionType.RatingList:
      return undefined;
    case QuestionType.RankingList:
      return 'Select Order of Rank';
    case QuestionType.TextboxGridNumeric:
      return getGridHelperText(qSchema, isDesktop) || 'Fill in all fields';
    case QuestionType.TextboxListNumeric:
      return 'Fill in all fields';
    case QuestionType.TextboxListText:
      return 'Fill in all fields';
    case QuestionType.ContinuousSumList:
      return 'The total must sum to 100%';
    default:
      throw Error(`Unknown QuestionType (${qSchema})`);
  }
}

function getGridHelperText(
  schema: GridQuestionSchema,
  isDesktop: boolean
): string | undefined {
  if (schema.requiredOptions) {
    switch (schema.requiredOptions.strategy) {
      case GridRequiredStrategy.All:
        return 'Fill in all fields';
      case GridRequiredStrategy.OnePerColumn:
        return isDesktop ? 'Select one per column' : 'Select one per section';
      case GridRequiredStrategy.OnePerRow:
        return isDesktop ? 'Select one per row' : 'Select one per section';
      case GridRequiredStrategy.AtLeastOnePerColumn:
        return isDesktop
          ? 'Select at least one per column'
          : 'Select at least one per section';
      case GridRequiredStrategy.AtLeastOnePerRow:
        return isDesktop
          ? 'Select at least one per row'
          : 'Select at least one per section';
      default:
        throw new Error('Unexpected GridRequiredStrategy type');
    }
  } else {
    return undefined;
  }
}
